import type { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'

import { CloseIcon } from '../components/icons'
import { deleteNotification, type AppNotification } from '../lib/notifications'

/**
 * Satir metni. `broadcast` DISINDAKI turler sabit; metin `kind`e gore i18n'den
 * uretilir. `broadcast` metnini bir INSAN (superadmin) yazdi ve `payload`
 * icinde geliyor - o yuzden i18n'den DEGIL, dogrudan payload'dan gosteriliyor.
 */
function renderText(item: AppNotification, t: TFunction) {
  if (item.kind === 'memory_added') {
    // Arka planda hafizaya yazilanlar (bkz. `services/memory_extract.py`):
    // sohbette arac karti olmadigi icin seffaflik BURADAN - kullanici ne
    // kaydedildigini gorup ayarlardan silebiliyor.
    const added = (item.payload?.items as string[] | undefined) ?? []
    const removed = (item.payload?.removed as string[] | undefined) ?? []
    return (
      <>
        <p className="notification-title">
          {added.length > 0
            ? t('notifications.memory_added.title')
            : t('notifications.memory_added.removedTitle')}
        </p>
        {added.length > 0 && (
          <ul className="notification-items">
            {added.map((text) => (
              <li key={text}>{text}</li>
            ))}
          </ul>
        )}
        {added.length > 0 && removed.length > 0 && (
          <p className="notification-body">{t('notifications.memory_added.removed')}</p>
        )}
        {removed.length > 0 && (
          <ul className="notification-items notification-items-removed">
            {removed.map((text) => (
              <li key={text}>{text}</li>
            ))}
          </ul>
        )}
        <Link className="notification-link" to="/settings#memory">
          {t('notifications.memory_added.manage')}
        </Link>
      </>
    )
  }
  if (item.kind === 'broadcast') {
    const title = String(item.payload?.title ?? '')
    const body = String(item.payload?.body ?? '')
    return (
      <>
        <p className="notification-title">{title || t('notifications.broadcast.fallback')}</p>
        {body && <p className="notification-body">{body}</p>}
      </>
    )
  }
  return (
    <>
      <p className="notification-title">{t(`notifications.${item.kind}.title`)}</p>
      <p className="notification-body">{t(`notifications.${item.kind}.body`, item.payload)}</p>
    </>
  )
}

/**
 * Bildirim satirlari - metin ISTEMCIDE `kind` + `payload`den uretiliyor.
 *
 * Sunucu hazir bir cumle GONDERMIYOR: kullanici dilini degistirdiginde
 * eski bildirimler yanlis dilde kalirdi (bkz. `services/notifications.py`
 * dosya bası yorumu).
 */
export function NotificationList({
  items,
  onChanged,
}: {
  items: AppNotification[]
  onChanged(): void
}) {
  const { t } = useTranslation()

  if (items.length === 0) {
    return <p className="notification-empty">{t('notifications.empty')}</p>
  }

  async function remove(id: string) {
    await deleteNotification(id).catch(() => undefined)
    onChanged()
  }

  return (
    <ul className="notification-list">
      {items.map((item) => (
        <li
          key={item.id}
          className={item.read_at ? 'notification-item' : 'notification-item notification-unread'}
        >
          <div>{renderText(item, t)}</div>
          <button
            type="button"
            className="btn-quiet"
            onClick={() => void remove(item.id)}
            aria-label={t('notifications.dismiss')}
          >
            <CloseIcon />
          </button>
        </li>
      ))}
    </ul>
  )
}
